import { Accounting } from "../../gateway/Accounting/Accounting";
import { AccountingPlanHttpGateway } from "../../gateway/AccountingPlan/AccountingPlanHttpGateway";
import { LocalStoreService } from "./LocalStoreService";

/**
 * Ponto único de verdade sobre o acesso ao Agente Consultivo (Central Consultiva).
 *
 * O acesso é OU do plano contratado OU do override por contabilidade
 * (`accounting.preferences.agenteConsultivo`). Quem já tem no plano não precisa do
 * override — ele existe para os clientes especiais que estão fora do plano.
 *
 * Classe estática, não service do Angular, porque precisa ser lida de `route-access.js` e
 * do `HomeRedirectService`, que rodam fora da injeção de dependência.
 *
 * O valor é espelhado no localStorage porque `redirectAuthenticatedFromAuth` e
 * `resolveRedirectWhenAccessDenied` rodam no `$transitions.onBefore` (`src/lib/app.run.js`),
 * antes de qualquer resolve — e depois de um `window.location.href` para o V2 a memória morre.
 * O acesso ao storage é preguiçoso e protegido: em navegação anônima ou em teste, o valor em
 * memória basta.
 */
export const CONSULTIVE_FEATURE = "AgenteConsultivo";
/**
 * O plano vendido como Agente Consultivo é identificado pelo NOME, não por uma feature.
 * Nenhum documento de `accountingPlans` tem a feature `AgenteConsultivo` contratada — o
 * plano "Agente Consultivo" existe como plano inteiro. Sem este reconhecimento, quem
 * comprou o produto caía na Carteira, como se não tivesse nada.
 */
export const CONSULTIVE_PLAN_NAME = "AGENTE CONSULTIVO";
const LS_KEY = "nucont:consultive-access";

export class ConsultiveAccessService {
    static _memory = null;
    static _store = undefined;

    static _getStore() {
        if (this._store === undefined) {
            try {
                this._store = new LocalStoreService();
            } catch (error) {
                this._store = null;
            }
        }
        return this._store;
    }

    /**
     * Calcula o acesso, memoiza e devolve. Chamado onde há contabilidade E plano em mãos:
     * o resolve de `nucont.accounting` e o login.
     */
    static resolve({ accountingData, accountingPlanEntity } = {}) {
        const byOverride =
            accountingData?.preferences?.agenteConsultivo === true;

        const hasAccess =
            byOverride || this.hasAccessByPlan(accountingPlanEntity);
        this._memory = hasAccess;
        try {
            this._getStore()?.setItem(LS_KEY, hasAccess);
        } catch (error) {
            // storage indisponível — a memória segue valendo nesta aba
        }
        return hasAccess;
    }

    /**
     * Resolve o acesso de OUTRA contabilidade, buscando contabilidade e plano.
     *
     * É o que o time interno precisa ao abrir a casa de um cliente pela lista: o `peek()` ali
     * responderia pelo acesso do nuconter logado, não pelo do cliente. E o campo da listagem
     * (`get-customers`) só conhece o override — uma contabilidade que tem o Agente pelo plano
     * seria mandada para o lugar errado.
     *
     * Não escreve no cache: o acesso guardado é o do usuário logado, e é dele que os
     * redirecionamentos dele dependem.
     */
    static async resolveFor({ accountingId } = {}) {
        if (!accountingId) return false;

        try {
            const accounting = await new Accounting().getById(accountingId);
            if (accounting?.preferences?.agenteConsultivo === true) return true;

            const contract = accounting?.contract;
            if (!contract) return false;

            const plan = await new AccountingPlanHttpGateway().getPlanByName(
                contract
            );
            return this.hasAccessByPlan(plan);
        } catch (error) {
            return false;
        }
    }

    /**
     * O plano dá acesso por dois caminhos: a feature `AgenteConsultivo`, se algum plano
     * passar a contratá-la, ou o próprio plano chamado "Agente Consultivo", que é como o
     * produto é vendido hoje.
     */
    static hasAccessByPlan(accountingPlanEntity) {
        try {
            if (
                accountingPlanEntity?.isContractedFeature({
                    value: CONSULTIVE_FEATURE
                }) === true
            )
                return true;
        } catch (error) {
            // plano sem features ou entidade incompleta — sobra o nome
        }

        const name =
            accountingPlanEntity?.props?.name_nd ||
            accountingPlanEntity?.props?.name;

        return (
            typeof name === "string" &&
            name.trim().toUpperCase() === CONSULTIVE_PLAN_NAME
        );
    }

    /** Leitura síncrona, para quem não tem contabilidade nem plano em mãos. */
    static peek() {
        if (this._memory !== null) return this._memory;
        try {
            return this._getStore()?.getItem(LS_KEY) === true;
        } catch (error) {
            return false;
        }
    }

    static clear() {
        this._memory = null;
        try {
            this._getStore()?.removeItem(LS_KEY);
        } catch (error) {
            // nada a limpar
        }
    }
}
